/**
 * Stack design tokens as plain JS — for React Native / React Native Web.
 *
 * This is a hand-mirrored port of tokens/*.css. RN can't read CSS custom
 * properties, @font-face, or box-shadow strings, so every value here is
 * resolved to something RN's StyleSheet API actually accepts:
 *  - colors: final hex/rgba strings (no var() chains)
 *  - spacing/radii/fontSize: unitless numbers (RN treats these as px)
 *  - shadows: split into iOS shadow* props + Android elevation, not CSS box-shadow
 *  - letterSpacing: RN wants px, CSS tokens are em — use trackingPx(fontSize, key) below
 *  - lineHeight: RN wants an absolute px number, not a unitless multiplier — use lineHeightPx(fontSize, key) below
 *  - easing: cubic-bezier control points as arrays, for use with Animated.Easing.bezier(...)
 *
 * If tokens/*.css changes, update this file to match by hand — there is no
 * build step wired up to regenerate it automatically.
 */

export const colors = {
  // Brand
  brandRed: '#FF194D',
  brandRedDark: '#D40F3C',
  brandRedDeep: '#A60F2C',
  brandRedTint: '#FFE3E9',
  brandBlue: '#006DEE',
  brandBlueDark: '#0052B4',
  brandBlueTint: '#DCEBFF',

  // Neutral scale
  gray0: '#FFFFFF',
  gray25: '#FAFAFA',
  gray50: '#F4F5F6',
  gray100: '#E9EAEC',
  gray200: '#DBDDE1',
  gray300: '#C2C5CB',
  gray400: '#9BA0A9',
  gray500: '#757B86',
  gray600: '#565C67',
  gray700: '#3D414A',
  gray800: '#24272D',
  gray900: '#15171B',
  gray950: '#0A0B0D',

  // Semantic surfaces
  surfacePage: '#FFFFFF',
  surfaceCanvas: '#FAFAFA',
  surfaceCard: '#FFFFFF',
  surfaceSunken: '#F4F5F6',
  surfaceInverse: '#0A0B0D',
  surfaceOverlay: 'rgba(10, 11, 13, 0.55)',

  // Semantic text
  textPrimary: '#0A0B0D',
  textSecondary: '#565C67',
  textTertiary: '#9BA0A9',
  textDisabled: '#C2C5CB',
  textInverse: '#FFFFFF',
  textLink: '#FF194D',
  textLinkHover: '#D40F3C',

  // Borders
  borderSubtle: '#E9EAEC',
  borderDefault: '#DBDDE1',
  borderStrong: '#C2C5CB',
  borderInverse: 'rgba(255, 255, 255, 0.16)',
  borderFocus: '#FF194D',

  // Actions / accent — primary is the logo pink; critical uses a deeper,
  // deliberately distinct red so destructive buttons never read as the CTA.
  accentPrimary: '#FF194D',
  accentPrimaryHover: '#D40F3C',
  accentPrimaryActive: '#A60F2C',
  accentOnPrimary: '#FFFFFF',

  accentCritical: '#A60F2C',
  accentCriticalHover: '#7D0A21',
  accentCriticalTint: '#FFE3E9',

  // Semantic status
  statusPositive: '#12805C',
  statusPositiveTint: '#E1F5EC',
  statusNegative: '#A60F2C',
  statusNegativeTint: '#FFE3E9',
  statusNeutral: '#757B86',
  statusNeutralTint: '#E9EAEC',
  statusInfo: '#006DEE',
  statusInfoTint: '#DCEBFF',

  // Chart series
  chart1: '#FF194D',
  chart2: '#24272D',
  chart3: '#006DEE',
  chart4: '#9BA0A9',
  chart5: '#6B8AFE',
  chart6: '#DBDDE1',
};

export const spacing = {
  space0: 0,
  space1: 4,
  space2: 8,
  space3: 12,
  space4: 16,
  space5: 20,
  space6: 24,
  space8: 32,
  space10: 40,
  space12: 48,
  space16: 64,
  space20: 80,
  space24: 96,
  space32: 128,
};

export const radii = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 10,
  xl: 16,
  full: 999,
};

export const borderWidth = {
  thin: 1,
  thick: 2,
};

/**
 * RN shadows are split per-platform. Use like:
 *   style={[styles.card, shadows.sm]}
 * shadows.sm already contains both the iOS shadow* keys and Android elevation.
 */
export const shadows = {
  xs: { shadowColor: '#0A0B0D', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 2, elevation: 1 },
  sm: { shadowColor: '#0A0B0D', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 2 },
  md: { shadowColor: '#0A0B0D', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.07, shadowRadius: 10, elevation: 4 },
  lg: { shadowColor: '#0A0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.10, shadowRadius: 24, elevation: 8 },
  focusRing: { shadowColor: '#FF194D', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.25, shadowRadius: 3, elevation: 0 },
};

export const typography = {
  // Font family names — must match the family names registered via
  // expo-font's useFonts({...}) in the consuming app.
  fontSans: 'IBMPlexSans',
  fontArchia: 'Archia',
  fontMono: 'IBMPlexMono',

  // Semantic aliases — Archia is Regular-weight only, never set it bold.
  fontDisplay: 'Archia',
  fontBody: 'IBMPlexSans',
  fontData: 'IBMPlexMono',
  fontLabel: 'IBMPlexMono',

  weight: {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },

  // Font sizes — unitless numbers, RN treats these as px.
  size: {
    '3xs': 11,
    '2xs': 12,
    xs: 13,
    sm: 14,
    base: 15,
    md: 17,
    lg: 20,
    xl: 24,
    '2xl': 32,
    '3xl': 40,
    '4xl': 56,
  },

  // Leading — unitless multipliers, same meaning as the CSS tokens.
  // RN's lineHeight prop wants an absolute px number: use lineHeightPx() below.
  leading: {
    tight: 1.15,
    snug: 1.35,
    normal: 1.55,
  },

  // Tracking — em values, same meaning as the CSS tokens.
  // RN's letterSpacing prop wants px: use trackingPx() below.
  tracking: {
    tight: -0.02,
    normal: 0,
    wide: 0.04,
    widest: 0.08,
  },
};

/** RN lineHeight wants an absolute px number: lineHeightPx(17, 'snug') -> 22.95 */
export function lineHeightPx(fontSize, leadingKey) {
  return fontSize * typography.leading[leadingKey];
}

/** RN letterSpacing wants px: trackingPx(32, 'tight') -> -0.64 */
export function trackingPx(fontSize, trackingKey) {
  return fontSize * typography.tracking[trackingKey];
}

export const motion = {
  duration: {
    fast: 100,
    base: 160,
    slow: 240,
  },
  // Cubic-bezier control points for Animated.Easing.bezier(...args)
  easing: {
    standard: [0.2, 0, 0, 1],
    out: [0.05, 0.7, 0.1, 1],
  },
};

export const theme = { colors, spacing, radii, borderWidth, shadows, typography, motion, lineHeightPx, trackingPx };

export default theme;
